<script setup>
import {useFetch} from "@/composables/useFetch.js";
import {onMounted, ref} from "vue";
import "@bhplugin/vue3-datatable/dist/style.css";
import {useRoute, useRouter} from "vue-router";
import {uri} from "@/constants/config.js";
import "vue-awesome-paginate/dist/style.css";
import {useI18n} from "vue-i18n";
import "v-network-graph/lib/style.css"
import cloudComputingImage from "@/assets/images/graph/cloud-computing.png"
import Button from "@/base-components/Button";
import DomainView from "@/pages/manager/cloud/list/views/DomainView.vue";
import Lucide from "@/base-components/Lucide";
import RegionView from "@/pages/manager/cloud/list/views/RegionView.vue";
import Template from "@/pages/Template.vue";
import NetworkDialog from "@/pages/manager/cloud/list/views/NetworkDialogView.vue";

const {t: $t} = useI18n()
const route = useRoute()
const router = useRouter()
const regionsList = ref([])
const cloud = ref(null)
const domains = ref([])
const isLoadingFlag = ref(true)

const getCloudList = async () => {
  isLoadingFlag.value = true
  let response = null
  try {
    const {data, error} = await useFetch(`${uri.admin.cloud.list}`, {method: "GET"});
    if (data) {
      response = data
    }
  } catch (err) {
    console.log(err)
  }
  return response
}

const getRegionList = async () => {
  let response = null
  try {
    const {data, error} = await useFetch(`${uri.admin.cloud.region.list}`, {method: "GET"});
    if (data) {
      response = data
    }
  } catch (err) {
    console.log(err)
  }
  return response
}

const cloudsTree = ref(null)
const fetchData = async () => {
  try {
    const [cloudTree, regions] = await Promise.all([
      getCloudList(),
      getRegionList(),
    ]);
    regionsList.value = regions['osRegions'];
    cloud.value = cloudTree.find(x => x.baseChamberId === null)
    domains.value = cloudTree.filter(x => x.baseChamberId === cloud.value.id)
  } catch (error) {
    console.error('Error fetching data:', error);
  } finally {
    isLoadingFlag.value = false
  }
}

const showNetworkDialog = ref(false)
const selectedRegion = ref(null)

function openNetworkDialog(osRegion) {
  selectedRegion.value = osRegion
  showNetworkDialog.value = true
}

// Fetch data when the component mounts
onMounted(fetchData);
</script>

<template>
  <section>
    <NetworkDialog v-if="showNetworkDialog" :show="showNetworkDialog" :region="selectedRegion"
                   @close="showNetworkDialog = false"/>
    <div
        v-if="!isLoadingFlag && cloud"
        class="rounded-md mb-3 p-3 gap-3"
        dir="ltr">
      <div
          class="grid grid-cols-6 gap-4 relative box rounded-lgspace-y-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
        <div class="col-span-2 p-6 border-r">
          <div class="flex items-center justify-between border-b pb-2 mb-4">
            <div class="w-full gap-2">
              <img
                  :src="cloudComputingImage"
                  alt="cloud"
                  class="h-16 -mt-12"
              />
              <h2 class="text-2xl font-semibold text-gray-800">{{ cloud.displayName }} </h2>
            </div>
            <div class="w-36 -mt-3 flex flex-row gap-2">
              <div class="text-sm text-gray-500">{{ cloud.name }}</div>
              <Button :variant="cloud.isActive===1?'primary':'outline-warning'" size="sm" class="w-16">
                {{ cloud.isActive === 1 ? $t("title.active") : $t("title.inactive") }}
              </Button>
            </div>
          </div>
          <div class="p-6 gap-4">
            <div class="flex flex-row mb-4">
              <div class="w-full">
                <p class="text-gray-500 text-sm">
                  Chamber Type
                </p>
                <p class="font-medium text-gray-800">
                  {{ cloud.chamberType }}
                </p>
              </div>
              <div class="w-36">
                <p class="text-gray-500 text-sm"></p>
                <p class="font-medium text-gray-800" v-if="cloud.isReseller===1">
                  Reseller
                </p>
              </div>
            </div>
            <!-- Bottom Data Fields - Configuration -->
            <div class="mt-4 ">
              <div class="flex flex-col mb-4">
                <p class="text-gray-500 text-sm">OS Port URL</p>
                <p class="font-medium text-gray-800 truncate">{{ (cloud.config).osPortUrl }}</p>
              </div>

              <div class="flex flex-col mb-4">
                <p class="text-gray-500 text-sm">OS Port Key</p>
                <p class="font-medium text-gray-800 truncate">{{ (cloud.config).osPortKey }}</p>
              </div>

              <div class="flex flex-col">
                <p class="text-gray-500 text-sm">Entry Points</p>
                <ul class="font-medium text-gray-800">
                  <li v-for="entryPoint in (cloud.config).entryPoints" :key="entryPoint">{{
                      entryPoint
                    }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="col-span-4">
          <div class="w-full my-3 flex flex-row  pe-6 lg:pe-6 xl:pe-0">
            <h3 class="text-base my-2 font-semibold w-32">
              Regions
            </h3>
          </div>
          <div class="w-full">
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-4 lg:grid-cols-3 gap-4">
              <free-style-shimmer v-if="isLoadingFlag" width="100%" height="300px" class="w-full"/>
              <free-style-shimmer v-if="isLoadingFlag" width="100%" height="300px" class="w-full"/>
              <free-style-shimmer v-if="isLoadingFlag" width="100%" height="300px" class="w-full"/>
              <template v-for="(x,i) in regionsList" :key="x.id">
                <RegionView v-model="regionsList[i]" @network="openNetworkDialog(regionsList[i])"/>
              </template>
              <!--          <RegionList :regions-list="regionsList"/>-->
            </div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-1 xl:grid-cols-1 lg:grid-cols-1 gap-4">
        <DomainView v-if="!isLoadingFlag" v-model="domains"/>
      </div>
    </div>
  </section>
</template>
